import clsx from 'clsx'

/**
 * Loading placeholders.
 *
 * Skeletons rather than spinners for anything over ~300ms: they reserve the
 * space the content will occupy, so the page does not jump when data arrives.
 * A spinner reserves nothing and every load ends in a layout shift.
 */

export function Skeleton({ className, style }) {
  return (
    <div
      className={clsx('animate-pulse rounded bg-[var(--color-line)]', className)}
      style={style}
      // Decorative: a screen reader should hear the loading announcement from
      // the region, not a stream of empty boxes.
      aria-hidden="true"
    />
  )
}

export function SkeletonText({ lines = 3, className }) {
  return (
    <div className={clsx('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className="h-3.5"
          // The last line runs short, the way a real paragraph does.
          style={{ width: index === lines - 1 ? '62%' : '100%' }}
        />
      ))}
    </div>
  )
}

export function SkeletonStats({ count = 4, className }) {
  return (
    <div className={clsx('grid gap-3 sm:grid-cols-2 lg:grid-cols-4', className)} aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-3.5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-2.5 h-7 w-16" />
        </div>
      ))}
    </div>
  )
}

/**
 * Wraps a loading region.
 *
 * Announces politely once, rather than letting a screen reader read out every
 * placeholder as it appears.
 */
export function LoadingRegion({ loading, label = 'Loading', children }) {
  return (
    <div aria-busy={loading || undefined} aria-live="polite">
      {loading && <span className="sr-only">{label}</span>}
      {children}
    </div>
  )
}
